import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { LuX } from "react-icons/lu";
import { SHORTCUTS, isShortcutActiveOn } from "../../utils/shortcuts";
import ShortcutHint from "./ShortcutHint";

const ShortcutsCheatsheet = ({ isAdmin, onClose }) => {
  const { pathname } = useLocation();

  // Capture-phase Escape handler so the cheatsheet closes *instead of* the page-level
  // BACK shortcut on routes that bind Escape (e.g. admission detail), not in addition to it.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopImmediatePropagation();
      onClose();
    };
    document.addEventListener("keydown", onKeyDown, { capture: true });
    return () => document.removeEventListener("keydown", onKeyDown, { capture: true });
  }, [onClose]);

  const groups = useMemo(() => {
    return Object.values(SHORTCUTS)
      .filter((shortcut) => (shortcut.role !== "admin" || isAdmin) && isShortcutActiveOn(shortcut, pathname))
      .reduce((acc, shortcut) => {
        acc[shortcut.group] = [...(acc[shortcut.group] || []), shortcut];
        return acc;
      }, {});
  }, [pathname, isAdmin]);

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/20" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-2xl overflow-auto rounded-lg bg-white p-6" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Raccourcis clavier</h2>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600">
            <LuX className="size-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-2">
          {Object.entries(groups).map(([group, shortcuts]) => (
            <div key={group}>
              <h3 className="mb-3 text-sm font-semibold text-gray-900">{group}</h3>
              <ul role="list" className="space-y-2">
                {shortcuts.map((shortcut) => (
                  <li key={shortcut.keys} className="flex items-center justify-between gap-4 text-sm text-gray-600">
                    <span>{shortcut.labelFr}</span>
                    <ShortcutHint keys={shortcut.keys} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ShortcutsCheatsheet;
